// src/components/draft/DraftFilters.js - Draft Filters Tile
//
// Pattern E discipline (8m.9): filters.search || '' and (filters.position
// || 'ALL') idioms at L14/19/93 intentionally use || (not ??). Empty-string-
// as-missing semantic for search input; 'ALL' as canonical "no filter"
// sentinel for position selector.
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { parseIntOrPrev } from '../../services/formUtils';

/**
 * Filters tile for available players
 */
const DraftFilters = ({
  players = [], // Used to extract unique positions
  filters = { position: 'ALL', search: '', minAB: 0, minG: 0, minIP: 0, minGS: 0, minGPitching: 0 },
  onFiltersChange
}) => {
  const [localSearch, setLocalSearch] = useState(filters.search || '');
  const searchTimeoutRef = useRef(null);

  // Sync local search with filters
  useEffect(() => {
    setLocalSearch(filters.search || '');
  }, [filters.search]);

  // Fixed position list — not derived from filtered players (which would
  // cause positions to disappear when a filter is active). Includes
  // CI/MI roster slot types for multi-position eligibility filtering.
  const positions = useMemo(() => {
    return ['ALL', 'C', '1B', '2B', '3B', 'SS', 'OF', 'CI', 'MI', 'DH', 'SP', 'RP'];
  }, []);

  const handleSearchChange = (value) => {
    setLocalSearch(value);
    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      onFiltersChange && onFiltersChange({
        ...filters,
        search: value
      });
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleFilterChange = (key, value) => {
    onFiltersChange && onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  // Filter options
  const minGOptions = [0, 10, 20, 50, 100, 150];
  const minABOptions = [0, 50, 100, 200, 300, 400, 500];
  const minGSOptions = [0, 5, 10, 15, 20, 25, 30];
  const minIPOptions = [0, 10, 30, 50, 100, 150, 200];

  const selectClass = `${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} rounded px-1 py-1 text-xs focus:outline-none`;

  return (
    <div className={`${dynastyTheme.components.card.base} p-3`}>
      <div className="space-y-2">
        {/* Row 1: Search + Position */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-neutral-400" />
            <input
              type="text"
              value={localSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search players..."
              className={`w-full pl-7 pr-6 py-1.5 rounded ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} focus:outline-none text-xs`}
            />
            {localSearch && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-white"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
        {/* Position pills — click active pill to deselect (return to ALL) */}
        <div className="flex flex-wrap gap-1">
          {positions.filter(p => p !== 'ALL').map(pos => {
            const isActive = (filters.position || 'ALL') === pos;
            return (
              <button
                key={pos}
                onClick={() => handleFilterChange('position', isActive ? 'ALL' : pos)}
                className={`px-2 py-0.5 rounded text-xs font-medium transition-all
                  ${isActive
                    ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark}`
                    : `${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.text.neutralLight} hover:bg-gray-700`
                  }`}
              >
                {pos}
              </button>
            );
          })}
        </div>

        {/* Row 2: Hitting filters */}
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${dynastyTheme.classes.text.neutralLight} w-12`}>Hitting</span>
          <select
            value={filters.minG ?? 0}
            onChange={(e) => handleFilterChange('minG', parseIntOrPrev(e.target.value, filters.minG))}
            className={`${selectClass} flex-1`}
          >
            {minGOptions.map(val => (
              <option key={val} value={val}>{val === 0 ? 'Min G' : `${val}+ G`}</option>
            ))}
          </select>
          <select
            value={filters.minAB ?? 0}
            onChange={(e) => handleFilterChange('minAB', parseIntOrPrev(e.target.value, filters.minAB))}
            className={`${selectClass} flex-1`}
          >
            {minABOptions.map(val => (
              <option key={val} value={val}>{val === 0 ? 'Min AB' : `${val}+ AB`}</option>
            ))}
          </select>
        </div>

        {/* Row 3: Pitching filters */}
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${dynastyTheme.classes.text.neutralLight} w-12`}>Pitching</span>
          <select
            value={filters.minGPitching ?? 0}
            onChange={(e) => handleFilterChange('minGPitching', parseIntOrPrev(e.target.value, filters.minGPitching))}
            className={`${selectClass} flex-1`}
          >
            {minGOptions.map(val => (
              <option key={val} value={val}>{val === 0 ? 'Min G' : `${val}+ G`}</option>
            ))}
          </select>
          <select
            value={filters.minGS ?? 0}
            onChange={(e) => handleFilterChange('minGS', parseIntOrPrev(e.target.value, filters.minGS))}
            className={`${selectClass} flex-1`}
          >
            {minGSOptions.map(val => (
              <option key={val} value={val}>{val === 0 ? 'Min GS' : `${val}+ GS`}</option>
            ))}
          </select>
          <select
            value={filters.minIP ?? 0}
            onChange={(e) => handleFilterChange('minIP', parseIntOrPrev(e.target.value, filters.minIP))}
            className={`${selectClass} flex-1`}
          >
            {minIPOptions.map(val => (
              <option key={val} value={val}>{val === 0 ? 'Min IP' : `${val}+ IP`}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default DraftFilters;

